/**
 * @file FooterTab.tsx
 * @description Вкладка «Подвал» (phase19.16 Р1; SB14, SB19, SB21;
 * uiux-design-r1 §2): шаблон «в строку» / «колонками», заливка, что
 * показывать (пустое значение — строка неактивна и ведёт его задать),
 * рисунок подвала из медиатеки, сведения о продавце — только показ.
 *
 * Приёмка Р2а (SB113–SB118; uiux-footer-columns §4.4, §5, §9): схема
 * колонок по фактическому меню; «что показывать» разложено по месту на
 * сайте. Решение 2026-10-11 (дополнение SB8, SB120): заливка — роль темы или
 * свой цвет; тон пишется вместе с заливкой.
 *
 * SB122 (uiux-settings-groups §4.1): шесть групп-карточек — Расположение,
 * Заливка, Колонка «О магазине», Колонки меню, Рисунок подвала, Нижняя
 * полоса; память свёрнутых — `commora.settings-groups.footer`, на ПК все
 * открыты, сверху оглавление; `?group=<id>` открывает группу.
 *
 * Партия «Юридические документы» (SB142): свои строки «Ссылок» — раздел темы
 * `footerDocs`; правка «Ссылок» уходит одной правкой (`onLinks`).
 */

import { fillContrastIssue, type SiteColors, type SiteFooterDocs, type SiteTheme } from "@chaiberi/shared";
import { Link } from "react-router";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { SurfaceFillField } from "../SurfaceFillField";
import { FooterBottomGroup } from "./FooterBottomGroup";
import { FooterDecorGroup } from "./FooterDecorGroup";
import { FooterLayoutGroup } from "./FooterLayoutGroup";
import type { FooterLinksPatch } from "./FooterLinksGroup";
import { FooterShowRow } from "./FooterShowRow";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { useFooterColumns } from "../../../hooks/site-view/useFooterColumns";
import { useSellerInfo } from "../../../hooks/site-view/useSellerInfo";
import { footerContactsCount, plural } from "../../../lib/site-view/footer-columns";
import { surfaceFillLabel } from "../../../lib/site-view/surface-fill-labels";
import type { SiteBrandDraft, SiteMedia } from "../../../lib/site-view/types";

type Footer = SiteTheme["footer"];
type AboutKey = "brand" | "tagline" | "contacts" | "signature";

interface Props {
  footer: Footer;
  brand: SiteBrandDraft;
  /** Цвета темы — для заливки и её контраста. */
  colors: SiteColors;
  onChange: (patch: Partial<Footer>) => void;
  onDecor: (media: SiteMedia | null) => void;
  /** Свои строки «Ссылок» нижней полосы (SB142). */
  footerDocs: SiteFooterDocs;
  /** Правка «Ссылок»: `footer.legal` и `footerDocs` одной правкой черновика. */
  onLinks: (patch: FooterLinksPatch) => void;
}

/** Строки колонки «О магазине»: подпись и значение (пустое → строка неактивна). */
function aboutRows(brand: SiteBrandDraft): Array<{ key: AboutKey; label: string; value: string | null }> {
  // Только те, что отмечены «показывать в подвале» (как на сайте).
  const contacts = footerContactsCount(brand);
  return [
    { key: "brand", label: "Логотип и название", value: brand.siteName || null },
    { key: "tagline", label: "Слоган", value: brand.tagline ? `«${brand.tagline}»` : null },
    { key: "contacts", label: `Контакты и соцсети · ${contacts}`, value: contacts > 0 ? "есть" : null },
    { key: "signature", label: "Подпись", value: brand.footerSignature ? `«${brand.footerSignature}»` : null },
  ];
}

export function FooterTab({ footer, brand, colors, onChange, onDecor, footerDocs, onLinks }: Props) {
  const link = useBuilderLink();
  const seller = useSellerInfo(brand.shopContactEmail);
  const columns = useFooterColumns(footer, brand);
  const settings = link({ panel: "settings", sub: "brand" });
  const menuHref = link({ panel: "menu", sub: "footer" });
  const setShow = (key: keyof Footer["show"], on: boolean) => onChange({ show: { ...footer.show, [key]: on } });
  const layout = columns.layout;
  const about = aboutRows(brand);
  const aboutShown = about.filter((row) => row.value !== null && footer.show[row.key]).length;
  const noDarkLogo = footer.tone === "dark" && !brand.logoDark;
  const contrast = fillContrastIssue(footer.fill, { colors });
  const menuColumns = layout?.visibleColumns ?? 0;
  const menuEmpty = layout !== null && layout.columns.length === 0;

  return (
    <SettingsGroups panel="footer">
      <FooterLayoutGroup footer={footer} columns={columns} menuHref={menuHref} onChange={onChange} />
      <SettingsGroup
        id="fill"
        title="Заливка"
        hint="Цвет фона подвала."
        summary={surfaceFillLabel(footer.fill)}
        status={noDarkLogo || (contrast !== null && !contrast.ok) ? "warning" : undefined}
      >
        <SurfaceFillField label="Заливка подвала" value={footer.fill} colors={colors} onChange={(fill, tone) => onChange({ fill, tone })} />
        {noDarkLogo ? (
          <NoticeStrip tone="warning">Подвал тёмный, а логотипа для тёмного фона нет — на сайте будет название текстом.</NoticeStrip>
        ) : null}
      </SettingsGroup>
      <SettingsGroup
        id="about"
        title="Колонка «О магазине»"
        tocLabel="О магазине"
        hint="Первая колонка подвала: логотип, слоган, контакты."
        summary={`${aboutShown} из ${about.length}`}
      >
        <div className="flex flex-col">
          {about.map((row) => (
            <FooterShowRow
              key={row.key}
              label={row.label}
              value={row.value}
              checked={footer.show[row.key]}
              onChange={(on) => setShow(row.key, on)}
              edit={settings}
            />
          ))}
        </div>
      </SettingsGroup>
      <SettingsGroup
        id="menu"
        title="Колонки меню"
        tocLabel="Меню"
        hint="Колонки ссылок из «Меню → Подвал»."
        summary={!footer.show.menu ? "выключены" : layout ? `${menuColumns} ${plural(menuColumns, "колонка", "колонки", "колонок")}` : "включены"}
      >
        <FooterShowRow
          label={layout ? `Меню подвала · ${menuColumns} ${plural(menuColumns, "колонка", "колонки", "колонок")}` : "Меню подвала"}
          value="есть"
          checked={footer.show.menu}
          onChange={(on) => setShow("menu", on)}
          edit={menuHref}
        />
        {footer.show.menu && menuEmpty ? (
          <div className="flex flex-col">
            <FieldHint className="mt-0">В меню подвала пока нет пунктов — покупатель увидит только «О магазине» и нижнюю полосу.</FieldHint>
            <Link to={menuHref} className="type-caption inline-flex min-h-12 items-center self-start font-medium text-primary hover:underline">
              Добавить пункты в «Меню → Подвал» →
            </Link>
          </div>
        ) : null}
      </SettingsGroup>
      <FooterDecorGroup footer={footer} brand={brand} onChange={onChange} onDecor={onDecor} />
      <FooterBottomGroup
        footer={footer}
        brand={brand}
        seller={seller}
        hiddenLinks={columns.hiddenLinks}
        legalDocs={columns.legalDocs}
        settingsHref={link({ panel: "settings", sub: "footer" })}
        onChange={onChange}
        footerDocs={footerDocs}
        onLinks={onLinks}
        legalTargets={columns.legalTargets}
        onLegalReload={columns.reloadLegal}
      />
    </SettingsGroups>
  );
}
